import type { ProductEvent, Options, Entity } from "@classdojo/log-client/lib/client/client";
import * as eventRecorder from "../components/devTools/eventRecorder";
import { sanitizeCheckoutSessionUrl } from "./sparks/sanitizeCheckoutSessionUrl";

const haveWindow = typeof window !== `undefined`;

let logclient: typeof import("@classdojo/log-client");

const getLogClient = async () => {
  if (!logclient) {
    logclient = await import("@classdojo/log-client");
  }
  return logclient;
};

const init = async (args: Options) => {
  if (!haveWindow) return;
  const initializedClient = (await getLogClient()).init(args);
  eventRecorder.start();

  return initializedClient;
};

const logEvent = async (event: string | ProductEvent) => {
  if (!haveWindow) return;
  return (await getLogClient()).logEvent(event);
};

const logMessage = async (logType: string, message: string) => {
  if (!haveWindow) return;
  return (await getLogClient()).logMessage(logType, message);
};

const setOnEvent = async (onEvent: (event: ProductEvent) => void): Promise<void> => {
  if (!haveWindow) return;

  (await getLogClient()).setOnEvent((event) => {
    onEvent(event);
  });
};

// DO NOT CALL DIRECTLY
// log the start of an experiment on mount using the useStartExperiment in src/utils/experiment
const _logStartExperiment = async (experiment: string, featureSwitches: Record<string, string>) => {
  if (!haveWindow) return;
  const logClient = await getLogClient();
  logClient.setFeatureSwitches(featureSwitches);
  logClient.setRoute(sanitizeCheckoutSessionUrl(window.location.href));
  return logClient.logStartExperiment(experiment);
};

// DO NOT CALL DIRECTLY — use useCreateConvertExperimentEvent from src/utils/experiment.
// Optional `featureSwitches` sets the variant map right before logging, for client-assigned
// experiments whose variant isn't in the API switch set.
const _logConvertExperiment = async (experiment: string, featureSwitches?: Record<string, string>) => {
  if (!haveWindow) return;
  const logClient = await getLogClient();
  if (featureSwitches) logClient.setFeatureSwitches(featureSwitches);
  return logClient.logConvertExperiment(experiment);
};

// Sets `experiment`'s variant on the log client's switch map, for the synchronous senders
// below. False, changing nothing, until the log client is loaded and initialized.
const setVariantNow = (experiment: string, variant: string): boolean => {
  if (!haveWindow || !logclient) return false;
  try {
    logclient.setFeatureSwitches({ ...logclient.getFeatureSwitches(), [experiment]: variant });
  } catch {
    // Loaded, but init() has not run yet.
    return false;
  }
  return true;
};

// DO NOT CALL DIRECTLY — the synchronous _logConvertExperiment, for a client-assigned
// variant, from a tap that is about to leave the page (an anchor, or a universal link that
// hands off to an app): the beacon is sent before the click handler returns, where
// getLogClient's `await` could let the page go first. Needs the log client already loaded
// (this module starts loading it on import, for _logException) and initialized
// (initLogClient, on the layout's first render); returns false, sending nothing, until then.
const _logConvertExperimentNow = (experiment: string, variant: string): boolean => {
  if (!setVariantNow(experiment, variant)) return false;
  logclient.logConvertExperiment(experiment);
  return true;
};

// DO NOT CALL DIRECTLY — the synchronous _logStartExperiment, for a client-assigned
// variant, for a tap that comes before the exposure has been logged (see
// _logConvertExperimentNow: same requirements, same false until they are met).
const _logStartExperimentNow = (experiment: string, variant: string): boolean => {
  if (!setVariantNow(experiment, variant)) return false;
  logclient.setRoute(sanitizeCheckoutSessionUrl(window.location.href));
  logclient.logStartExperiment(experiment);
  return true;
};

const setEntityId = async (entityId: string) => {
  if (!haveWindow) return;
  return (await getLogClient()).setEntityId(entityId);
};

const setEntityType = async (userData: { type?: Entity; student?: boolean }) => {
  if (!haveWindow) return;

  let entityType: Entity;

  if (userData.type === "parent" || userData.type === "teacher") {
    entityType = userData.type;
  } else if (userData.student) {
    entityType = "studentUser";
  } else {
    entityType = "loggedOut";
  }

  return (await getLogClient()).setEntityType(entityType);
};

const setFeatureSwitches = async (featureSwitches: Record<string, string>) => {
  if (!haveWindow) return;
  return (await getLogClient()).setFeatureSwitches(featureSwitches);
};

const setFeatureSetId = async (featureSetId: string) => {
  if (!haveWindow) return;
  return (await getLogClient()).setFeatureSetId(featureSetId);
};

const getSessionId = async () => {
  if (!haveWindow) return;
  return (await getLogClient()).getSessionId();
};

// The session id, synchronously, or undefined until the log client has loaded (see
// _logConvertExperimentNow).
const getSessionIdNow = (): string | undefined => (haveWindow && logclient ? logclient.getSessionId() : undefined);

const useGetSessionIdSync = (): string => {
  if (logclient) {
    return logclient.getSessionId();
  }
  return "";
};

const sendMetrics = async (metrics: any[]) => {
  if (!haveWindow) return;
  return (await getLogClient()).sendMetrics(metrics);
};

export declare type Tags = {
  [tagName: string]: string;
};
const incrementMetric = async (metricName: string, tags?: Tags) => {
  if (!haveWindow) return;
  return (await getLogClient()).sendMetrics([
    {
      type: "increment",
      value: 1,
      metricName: metricName,
      tags: tags,
    },
  ]);
};

type LogExceptionArgs = [
  error: Error,
  info:
    | string[]
    | {
        [key: string]: any;
      },
];

const _logException = haveWindow
  ? (async () => {
      return (await getLogClient()).logException;
    })()
  : Promise.resolve((...args: LogExceptionArgs) => {
      console.error("LOG_EXCEPTION_NO_WINDOW:", ...args);
    });

const logException = async (...args: LogExceptionArgs) => {
  const f = await _logException;
  f(...args);
};

// give this a unique name from the underlying logClient.logException so autocomplete/autoimport
// works more accurately
const sendException = logException;

export {
  init,
  logEvent,
  logMessage,
  incrementMetric,
  setEntityId,
  getSessionId,
  getSessionIdNow,
  useGetSessionIdSync,
  logException,
  sendException,
  setFeatureSetId,
  setOnEvent,
  _logStartExperiment,
  _logConvertExperiment,
  _logConvertExperimentNow,
  _logStartExperimentNow,
  setFeatureSwitches,
  sendMetrics,
  setEntityType,
};
export type { ProductEvent };
